import { Injectable } from '@angular/core';
import * as i18nIsoCountries from 'i18n-iso-countries';
import { forkJoin, Observable, of, tap } from 'rxjs';
import { UtilityClient } from '../clients/oreg-api/utility.client';
import { Country } from '../models/country.model';
import { UtilitiesRepository } from '../repositories/utilities.repository';

@Injectable({ providedIn: 'root' })
export class UtilitiesQuery {
  constructor(
    private utilitiesRepository: UtilitiesRepository,
    private utilityClient: UtilityClient,
  ) {}

  public loadUtilities(language: string): Observable<unknown> {
    return forkJoin([
      this.loadCounties(),
      this.loadMunicipalities(),
      this.loadCountries(language),
    ]);
  }

  public getMunicipalitiesFromCounty(countyCode: string) {
    return countyCode
      ? this.utilitiesRepository
          .getMunicipalities()
          .filter((m) => m.value.startsWith(countyCode))
      : this.utilitiesRepository.getMunicipalities();
  }

  public getCountyFromMunicipality(municipalityCode: string) {
    if (municipalityCode) {
      const countyCode =
        municipalityCode.charAt(0) + municipalityCode.charAt(1);
      return [
        this.utilitiesRepository
          .getCounties()
          .find((c) => c.value === countyCode),
      ];
    } else {
      return this.utilitiesRepository.getCounties();
    }
  }

  public getCountries(includeSweden: boolean) {
    const countries = this.utilitiesRepository.getCountries();

    if (includeSweden) {
      return countries;
    }

    return countries.filter((c) => c.value !== 'SE');
  }

  private loadMunicipalities() {
    return this.utilityClient.fetchMunicipalities().pipe(
      tap((municipalities) => {
        const sorted = municipalities.sort((a, b) =>
          a.label > b.label ? 1 : -1,
        );
        this.utilitiesRepository.setMunicipalities(sorted);
      }),
    );
  }

  private loadCounties() {
    return this.utilityClient
      .fetchCounties()
      .pipe(tap((counties) => this.utilitiesRepository.setCounties(counties)));
  }

  private loadCountries(language: string) {
    const countriesObject = i18nIsoCountries.getNames(language);
    const countries = new Array<Country>();
    for (const key of Object.keys(countriesObject)) {
      countries.push({
        value: key,
        label: countriesObject[key],
      } as Country);
    }
    const sortedCountries = countries.sort((a, b) =>
      a.label > b.label ? 1 : a.label < b.label ? -1 : 0,
    );
    this.utilitiesRepository.setCountries(sortedCountries);
    return of(true);
  }
}
